/**
 * UpdateWeightModal — logs a new weight measurement. The unit toggle converts
 * the entered value in place, exactly like the mobile bottom sheet.
 */
import { useState } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import {
  LBS_PER_KG,
  normalizeWeightUnit,
  type WeightUnitCode,
} from '@wlb/shared/utils/weightUtils';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { FormError } from '../../components/ui/FormError';
import './more.css';

const UNITS: readonly WeightUnitCode[] = ['kg', 'lbs'];

interface UpdateWeightModalProps {
  initialWeight?: number | null;
  initialUnit?: string | null;
  saving: boolean;
  onClose: () => void;
  onSave: (weight: number, unit: WeightUnitCode) => void;
}

const convert = (value: number, from: WeightUnitCode, to: WeightUnitCode): number => {
  if (from === to) return value;
  const converted = to === 'kg' ? value / LBS_PER_KG : value * LBS_PER_KG;
  return Number(converted.toFixed(1));
};

export function UpdateWeightModal({
  initialWeight,
  initialUnit,
  saving,
  onClose,
  onSave,
}: UpdateWeightModalProps) {
  const { t } = useTranslation();
  const [unit, setUnit] = useState<WeightUnitCode>(normalizeWeightUnit(initialUnit));
  const [value, setValue] = useState(
    initialWeight ? String(Number(initialWeight).toFixed(1)) : '',
  );
  const [error, setError] = useState('');

  const changeUnit = (next: WeightUnitCode) => {
    if (next === unit) return;
    const parsed = parseFloat(value);
    if (Number.isFinite(parsed)) setValue(String(convert(parsed, unit, next)));
    setUnit(next);
  };

  const handleSave = () => {
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError(t('tracker.bottomSheet.invalidWeight'));
      return;
    }
    setError('');
    onSave(parsed, unit);
  };

  return (
    <Modal
      title={t('tracker.bottomSheet.title')}
      onClose={saving ? () => undefined : onClose}
      labelledBy="update-weight-title"
    >
      <div className="tracker-weight-form">
        <p className="tracker-weight-form__description">
          {t('tracker.bottomSheet.description')}
        </p>

        <div className="tracker-weight-form__row">
          <input
            id="update-weight-input"
            className={`tracker-weight-form__input${error ? ' tracker-weight-form__input--error' : ''}`}
            type="number"
            inputMode="decimal"
            step="0.1"
            min="0"
            value={value}
            placeholder={t('tracker.bottomSheet.inputPlaceholder')}
            aria-label={t('tracker.bottomSheet.inputLabel')}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'update-weight-error' : undefined}
            onChange={event => setValue(event.target.value)}
          />
          <div className="tracker-units" role="group" aria-label={t('tracker.bottomSheet.inputLabel')}>
            {UNITS.map(option => (
              <button
                key={option}
                type="button"
                aria-pressed={unit === option}
                className={`tracker-units__option${unit === option ? ' tracker-units__option--active' : ''}`}
                onClick={() => changeUnit(option)}
              >
                {t(option === 'kg' ? 'tracker.bodyWeight.unitKg' : 'tracker.bodyWeight.unitLbs')}
              </button>
            ))}
          </div>
        </div>

        <FormError message={error} id="update-weight-error" />

        <Button fullWidth disabled={saving} onClick={handleSave}>
          {t(saving ? 'common.buttons.saving' : 'tracker.bottomSheet.saveButton')}
        </Button>
      </div>
    </Modal>
  );
}
